import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getAfterleaseVehicleCalculator,
  getCalculatorsDataByAttractivenessLevel,
  getNewVehicleCalculator
} from "../../../../services";
import type { CalculatorData, LeasingFormOptions, RentFormOptions } from "../types/calculatorData";
import {
  buildAdditionalServiceItemsFromAdditions,
  type AdditionalServiceItem,
  type PackageState,
  useAdditionalPackages
} from "../utils/additionalPackagesService";
import { applyCalculatorRadioChange, CalculatorRadioChangeEvent } from "../utils/applyCalculatorRadioChange";
import { appendBrokerUrlParams } from "../utils/appendBrokerUrlParams";
import { buildCalcOptionsForContactUrl } from "../utils/buildCalcOptionsForContactUrl";
import {
  buildPrintOfferPayload,
  type CalculatorContactFormDataPayload,
  type PrintOfferPayload
} from "../utils/buildPrintOfferPayload";
import { createURLToContactSection } from "../utils/createURLToContactSection";
import {
  getSelectedPricesFromCalculatorData,
  type SelectedCalculatorPrices
} from "../utils/getSelectedCalculatorPrice";
import {
  mapCalculatorDataToFormOptions,
  mapLeasingPeriodsToFormOptions,
  mapRentPeriodsToFormOptions
} from "../utils/mapCalculatorFormOptions";
import { getPackageValueForKind, PackageKind } from "../utils/packageEnumUtils";

export interface CalculatorSnapshot {
  calculatorData: CalculatorData;
  selectedPrices: SelectedCalculatorPrices | null;
  activePackageState: PackageState;
  activeTab: number;
  installmentPrice: number | undefined;
  installmentPriceWithoutPackages: number | null;
  urlObject: string;
  contactFormPayload: CalculatorContactFormDataPayload | null;
}

export interface UseCalculatorDataParams {
  carId: number;
  pageType: string;
  isRentAvailable: boolean;
  isLeasingAvailable: boolean;
  isCashAvailable: boolean;
  isElectric: boolean;
  literals?: Record<string, string | undefined> | null;
  attractivenessLevel?: string | null;
  templateId?: string | null;
  vendorType?: string | null;
  login?: string | null;
  onActiveTabChange?: (tab: number) => void;
  onContactUrl?: (url: string) => void;
  onCalculatorSnapshot?: (snapshot: CalculatorSnapshot) => void;
  totalPriceBrutto: number;
}

function buildSelectedServicesParam(packageState: PackageState): string {
  return Object.values(packageState)
    .filter((entry) => entry.isChecked)
    .map((entry) => entry.Value)
    .sort((a, b) => a - b)
    .join(",");
}

function filterInfoPackageItems(
  items: AdditionalServiceItem[],
  pageType: string,
  activeTab: number
): AdditionalServiceItem[] {
  if (pageType === "new" && activeTab === 1) {
    const prestigeNonAsoValue = getPackageValueForKind(PackageKind.ManagementPrestigeNonAso);
    const prestigeAsoValue = getPackageValueForKind(PackageKind.ManagementPrestige);
    const hasTwoPrestigePackage = [prestigeNonAsoValue, prestigeAsoValue].every((val) =>
      items.some((obj) => obj.Value === val)
    );
    const filtered = items.filter((pkg) => pkg.Value !== prestigeNonAsoValue);
    return hasTwoPrestigePackage ? filtered : items;
  }
  return items;
}

function getInitialTab(
  isCompany: boolean,
  isCashAvailable: boolean,
  isLeasingAvailable: boolean,
  isRentAvailable: boolean
): number {
  if (!isCompany && isCashAvailable) {
    return 3;
  }

  const rememberedTab = sessionStorage.getItem("mem_calc_tab");
  const compareLeaseToRent = sessionStorage.getItem("lowestInstallment");

  if (rememberedTab) {
    return Number(rememberedTab);
  }

  if (isLeasingAvailable && compareLeaseToRent === "lease") {
    return 2;
  }
  if (isRentAvailable) {
    return 1;
  }
  if (isLeasingAvailable) {
    return 2;
  }
  return 3;
}

const EMPTY_CALCULATOR_DATA: CalculatorData = {
  RentPeriods: [],
  RentAdditions: {
    Configuration: [],
    Rent_ManagementBasic: [],
    Rent_ManagementComfort: [],
    Rent_ManagementPrestige: [],
    Rent_ManagementPrestigeAso: [],
    Tires: [],
    Assistance: 0,
    PetrolCard: 0
  },
  LeasingPeriods: [],
  LeasingAdditions: { Configuration: [], Assistance: 0, TechnicalService: 0, Tires: 0, PetrolCard: 0 },
  Insurance: [],
  InsuranceAdditionalText: null
};

async function fetchCalculatorData(carId: number, pageType: string): Promise<CalculatorData | null> {
  const response =
    pageType === "new"
      ? await getNewVehicleCalculator(carId)
      : await getAfterleaseVehicleCalculator(carId);

  if (response.status >= 200 && response.status <= 299 && response.data) {
    return response.data as CalculatorData;
  }
  return null;
}

export function useCalculatorData(params: UseCalculatorDataParams) {
  const {
    carId,
    pageType,
    isRentAvailable,
    isLeasingAvailable,
    isCashAvailable,
    isElectric,
    literals,
    attractivenessLevel,
    templateId,
    vendorType,
    login,
    onActiveTabChange,
    onContactUrl,
    onCalculatorSnapshot,
    totalPriceBrutto
  } = params;

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [showCalc, setShowCalc] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [calcOptionsRaw, setCalcOptionsRaw] = useState<CalculatorData & { AuctionId: string } | null>(null);
  const [rentCalculatorData, setRentCalculatorData] = useState<RentFormOptions | null>(null);
  const [leasingCalculatorData, setLeasingCalculatorData] = useState<LeasingFormOptions | null>(null);
  const [installmentPrice, setInstallmentPrice] = useState<number | undefined>(undefined);
  const [installmentPriceWithoutPackages, setInstallmentPriceWithoutPackages] = useState<number | null>(null);
  const [urlObject, setUrlObject] = useState("");

  const onActiveTabChangeRef = useRef(onActiveTabChange);
  const onContactUrlRef = useRef(onContactUrl);
  const onCalculatorSnapshotRef = useRef(onCalculatorSnapshot);
  const lastSnapshotKeyRef = useRef("");

  onActiveTabChangeRef.current = onActiveTabChange;
  onContactUrlRef.current = onContactUrl;
  onCalculatorSnapshotRef.current = onCalculatorSnapshot;

  const applyCalculatorState = useCallback((data: CalculatorData) => {
    const withAuctionId = { ...data, AuctionId: String(carId) };
    const formOptions = mapCalculatorDataToFormOptions(data);
    setCalcOptionsRaw(withAuctionId);
    setRentCalculatorData(formOptions.rent);
    setLeasingCalculatorData(formOptions.leasing);
    setIsError(false);
  }, [carId]);

  const loadBaseCalculator = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchCalculatorData(carId, pageType);
      if (data) {
        applyCalculatorState(data);
      } else {
        setIsError(true);
      }
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [carId, pageType, applyCalculatorState]);

  useEffect(() => {
    loadBaseCalculator();
  }, [loadBaseCalculator]);

  const calculatorDataForPackages = calcOptionsRaw ?? EMPTY_CALCULATOR_DATA;

  const {
    activePackageState,
    packageTiles,
    installmentWithPackages
  } = useAdditionalPackages({
    calculatorData: calculatorDataForPackages,
    pageType,
    activeTab,
    isRentAvailable,
    isElectric,
    literals
  });

  const selectedServices = useMemo(
    () => buildSelectedServicesParam(activePackageState),
    [activePackageState]
  );

  useEffect(() => {
    if (!templateId || !attractivenessLevel) {
      return;
    }

    if (attractivenessLevel === "0") {
      loadBaseCalculator();
      return;
    }

    let cancelled = false;

    getCalculatorsDataByAttractivenessLevel(
      carId,
      templateId,
      attractivenessLevel,
      vendorType,
      login,
      selectedServices
    )
      .then(async (response) => {
        if (cancelled || !response?.data) {
          return;
        }

        const baseData = await fetchCalculatorData(carId, pageType);
        if (!baseData) {
          return;
        }

        const merged = { ...baseData };
        Object.keys(response.data).forEach((key) => {
          if (response.data.hasOwnProperty(key)) {
            (merged as Record<string, unknown>)[key] = response.data[key];
          }
        });
        applyCalculatorState(merged);
      })
      .catch((error) => {
        console.error(error);
      });

    return () => {
      cancelled = true;
    };
  }, [
    attractivenessLevel,
    templateId,
    vendorType,
    login,
    carId,
    pageType,
    selectedServices,
    loadBaseCalculator,
    applyCalculatorState
  ]);

  const persistActiveTab = useCallback((tab: number) => {
    setActiveTab((prev) => {
      if (prev === tab) {
        return prev;
      }
      sessionStorage.setItem("mem_calc_tab", String(tab));
      onActiveTabChangeRef.current?.(tab);
      return tab;
    });
  }, []);

  useEffect(() => {
    lastSnapshotKeyRef.current = "";
  }, [carId]);

  useEffect(() => {
    const initialTab = getInitialTab(true, isCashAvailable, isLeasingAvailable, isRentAvailable);
    persistActiveTab(initialTab);
  }, [isCashAvailable, isLeasingAvailable, isRentAvailable, persistActiveTab]);

  const handleRentRadioChange = useCallback(
    (event: CalculatorRadioChangeEvent) => {
      if (!calcOptionsRaw) return;
      const newRaw = applyCalculatorRadioChange(JSON.parse(JSON.stringify(calcOptionsRaw)), event);
      setCalcOptionsRaw({ ...newRaw, AuctionId: calcOptionsRaw.AuctionId });
      setRentCalculatorData(mapRentPeriodsToFormOptions(newRaw.RentPeriods));
    },
    [calcOptionsRaw]
  );

  const handleLeasingRadioChange = useCallback(
    (event: CalculatorRadioChangeEvent) => {
      if (!calcOptionsRaw) return;
      const newRaw = applyCalculatorRadioChange(JSON.parse(JSON.stringify(calcOptionsRaw)), event);
      setCalcOptionsRaw({ ...newRaw, AuctionId: calcOptionsRaw.AuctionId });
      setLeasingCalculatorData(mapLeasingPeriodsToFormOptions(newRaw.LeasingPeriods));
    },
    [calcOptionsRaw]
  );

  useEffect(() => {
    if (!calcOptionsRaw || !rentCalculatorData || !leasingCalculatorData) {
      return;
    }

    const calcOptions = buildCalcOptionsForContactUrl(rentCalculatorData, leasingCalculatorData);
    const isAfterLease = pageType === "afterLease";
    const url = appendBrokerUrlParams(
      createURLToContactSection(
        calcOptions,
        { AuctionId: calcOptionsRaw.AuctionId, LeasingPeriods: calcOptionsRaw.LeasingPeriods },
        activePackageState,
        activeTab,
        isAfterLease
      ),
      pageType,
      vendorType,
      login
    );

    setUrlObject((prev) => (prev === url ? prev : url));
    onContactUrlRef.current?.(url);
  }, [rentCalculatorData, leasingCalculatorData, activeTab, calcOptionsRaw, activePackageState, pageType, vendorType, login]);

  useEffect(() => {
    if (!calcOptionsRaw || !rentCalculatorData || !leasingCalculatorData) return;

    const prices = getSelectedPricesFromCalculatorData(calcOptionsRaw, activeTab);
    const baseInstallmentPrice = prices?.installmentPrice;
    if (baseInstallmentPrice == null) {
      return;
    }

    const withPackages = installmentWithPackages ?? baseInstallmentPrice;
    setInstallmentPrice((prev) => (prev === withPackages ? prev : withPackages));
    setInstallmentPriceWithoutPackages((prev) =>
      prev === baseInstallmentPrice ? prev : baseInstallmentPrice
    );

    const calcOptions = buildCalcOptionsForContactUrl(rentCalculatorData, leasingCalculatorData);
    const contactFormPayload =
      buildPrintOfferPayload(
        calcOptions,
        calcOptionsRaw.AuctionId,
        activePackageState,
        prices,
        activeTab,
        pageType === "afterLease",
        withPackages,
        totalPriceBrutto
      )?.CalculatorContactFormData ?? null;

    const snapshotKey = [
      activeTab,
      withPackages,
      baseInstallmentPrice,
      urlObject,
      Object.values(activePackageState)
        .filter((entry) => entry.isChecked)
        .map((entry) => entry.Value)
        .sort()
        .join(",")
    ].join("|");

    if (snapshotKey === lastSnapshotKeyRef.current) {
      return;
    }

    lastSnapshotKeyRef.current = snapshotKey;

    onCalculatorSnapshotRef.current?.({
      calculatorData: calcOptionsRaw,
      selectedPrices: prices,
      activePackageState,
      activeTab,
      installmentPrice: withPackages,
      installmentPriceWithoutPackages: baseInstallmentPrice,
      urlObject,
      contactFormPayload
    });
  }, [
    calcOptionsRaw,
    rentCalculatorData,
    leasingCalculatorData,
    activeTab,
    installmentWithPackages,
    activePackageState,
    urlObject,
    pageType,
    totalPriceBrutto
  ]);

  const infoPackageItems = useMemo(() => {
    const additions =
      pageType === "new" && activeTab === 1
        ? calculatorDataForPackages.RentAdditions
        : activeTab === 1
          ? calculatorDataForPackages.RentAdditions
          : calculatorDataForPackages.LeasingAdditions;

    const items = buildAdditionalServiceItemsFromAdditions(
      additions,
      literals,
      isElectric,
      activeTab,
      pageType,
      pageType === "afterLease" && activeTab === 1
        ? calculatorDataForPackages.LeasingAdditions
        : undefined
    );
    return filterInfoPackageItems(items, pageType, activeTab);
  }, [calculatorDataForPackages, pageType, activeTab, literals, isElectric]);

  const selectedPrices = calcOptionsRaw ? getSelectedPricesFromCalculatorData(calcOptionsRaw, activeTab) : null;

  const printOfferPayload = useMemo((): PrintOfferPayload | null => {
    if (!calcOptionsRaw || !rentCalculatorData || !leasingCalculatorData) {
      return null;
    }

    const calcOptions = buildCalcOptionsForContactUrl(rentCalculatorData, leasingCalculatorData);
    const isAfterLease = pageType === "afterLease";

    return buildPrintOfferPayload(
      calcOptions,
      calcOptionsRaw.AuctionId,
      activePackageState,
      selectedPrices,
      activeTab,
      isAfterLease,
      installmentPrice,
      totalPriceBrutto
    );
  }, [
    calcOptionsRaw,
    rentCalculatorData,
    leasingCalculatorData,
    activePackageState,
    selectedPrices,
    activeTab,
    pageType,
    installmentPrice,
    totalPriceBrutto
  ]);

  return {
    isLoading,
    isError,
    showCalc,
    setShowCalc,
    activeTab,
    persistActiveTab,
    calcOptionsRaw,
    rentCalculatorData,
    leasingCalculatorData,
    handleRentRadioChange,
    handleLeasingRadioChange,
    activePackageState,
    packageTiles,
    installmentPrice,
    installmentPriceWithoutPackages,
    selectedPrices,
    infoPackageItems,
    urlObject,
    printOfferPayload,
    carState: pageType === "new" ? "new" : "afterLease",
    vendorType
  };
}
